/**
 * Enum-derived UI option constants
 * All values reference @porchpass/api-client enums as the single source of truth.
 */

import {
  DomainAssetType,
  DomainBorrowerEmploymentStatus,
  DomainDownPaymentSourceType,
  DomainEthnicityType,
  DomainExpenseType,
  DomainFrequencyType,
  DomainHousingType,
  DomainIncomeType,
  DomainLandSituationType,
  DomainManufacturerType,
  DomainRaceType,
} from "@porchpass/api-client";

// ============================================================================
// Employment
// ============================================================================

export const EMPLOYMENT_STATUS_OPTIONS = [
  {
    value: DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed,
    label: "Employed",
  },
  {
    value: DomainBorrowerEmploymentStatus.BorrowerEmploymentUnemployed,
    label: "Unemployed",
  },
  {
    value: DomainBorrowerEmploymentStatus.BorrowerEmploymentRetired,
    label: "Retired",
  },
  {
    value: DomainBorrowerEmploymentStatus.BorrowerEmploymentOther,
    label: "Other",
  },
];

export const COMPENSATION_TYPE_OPTIONS = [
  { value: DomainFrequencyType.FrequencyHourly, label: "Hourly" },
  { value: DomainFrequencyType.FrequencyAnnual, label: "Salary" },
];

// Income frequency options (for bonus/commission/overtime dropdowns)
export const INCOME_FREQUENCY_OPTIONS = [
  { value: DomainFrequencyType.FrequencyWeekly, label: "Weekly" },
  { value: DomainFrequencyType.FrequencyBiWeekly, label: "Biweekly" },
  { value: DomainFrequencyType.FrequencyMonthly, label: "Monthly" },
  { value: DomainFrequencyType.FrequencyAnnual, label: "Annually" },
];

// ============================================================================
// Housing
// ============================================================================

export const HOUSING_TYPE_OPTIONS = [
  { value: DomainHousingType.HousingOwn, label: "Own" },
  { value: DomainHousingType.HousingRent, label: "Renting" },
  {
    value: DomainHousingType.HousingNoPrimaryExpense,
    label: "No housing expense",
  },
  { value: DomainHousingType.HousingOther, label: "Other" },
];

// ============================================================================
// Financial Accounts (Asset Types)
// ============================================================================

export const ACCOUNT_TYPES = [
  { value: DomainAssetType.AssetChecking, label: "Checking" },
  { value: DomainAssetType.AssetSavings, label: "Savings" },
  { value: DomainAssetType.AssetBonds, label: "Bonds" },
  {
    value: DomainAssetType.AssetCashValueOfLifeInsurance,
    label: "Cash Value of Life Insurance",
  },
  {
    value: DomainAssetType.AssetCertificateOfDeposit,
    label: "Certificate of Deposit",
  },
  { value: DomainAssetType.AssetMutualFunds, label: "Mutual Funds" },
  { value: DomainAssetType.AssetOtherRealEstate, label: "Other Real Estate" },
  { value: DomainAssetType.AssetRetirement, label: "Retirement (401k, IRA)" },
  { value: DomainAssetType.AssetStocks, label: "Stocks" },
  { value: DomainAssetType.AssetTrustAccount, label: "Trust Account" },
  { value: DomainAssetType.AssetOther, label: "Other" },
];

// ============================================================================
// Additional Income (non-employment income types)
// ============================================================================

// Note: BASE, BONUS, COMMISSION, OVERTIME are employment income types handled
// separately in the employment form, so they are excluded here.
export const INCOME_TYPES = [
  { value: DomainIncomeType.IncomeAlimony, label: "Alimony" },
  { value: DomainIncomeType.IncomeChildSupport, label: "Child Support" },
  { value: DomainIncomeType.IncomeRental, label: "Rental Income" },
  { value: DomainIncomeType.IncomeSSI, label: "Social Security (SSI)" },
  {
    value: DomainIncomeType.IncomeSSIForDependents,
    label: "SSI for Dependents",
  },
  { value: DomainIncomeType.IncomeDisability, label: "Disability" },
  { value: DomainIncomeType.IncomeVADisability, label: "VA Disability" },
  { value: DomainIncomeType.IncomeRetirement, label: "Retirement / Pension" },
  { value: DomainIncomeType.IncomeWIC, label: "WIC" },
  { value: DomainIncomeType.IncomeTANF, label: "TANF" },
  { value: DomainIncomeType.IncomeSNAP, label: "SNAP" },
  { value: DomainIncomeType.IncomeOther, label: "Other" },
];

// ============================================================================
// Expenses
// ============================================================================

export const EXPENSE_TYPES = [
  { value: DomainExpenseType.ExpenseAlimony, label: "Alimony" },
  { value: DomainExpenseType.ExpenseChildSupport, label: "Child Support" },
  { value: DomainExpenseType.ExpenseGarnishment, label: "Garnishment" },
  {
    value: DomainExpenseType.ExpenseOtherSupport,
    label: "Other Recurring Expense",
  },
];

// ============================================================================
// Land Situation
// ============================================================================

export const LAND_OPTIONS = [
  {
    value: DomainLandSituationType.LandSituationTypeAlreadyOwned,
    label: "Owned by borrower",
  },
  {
    value: DomainLandSituationType.LandSituationTypeCurrentlyFinanced,
    label: "Financed by borrower",
  },
  {
    value: DomainLandSituationType.LandSituationTypePurchasing,
    label: "Borrower plans to purchase",
  },
  {
    value: DomainLandSituationType.LandSituationTypeLeasing,
    label: "Borrower plans to lease",
  },
  {
    value: DomainLandSituationType.LandSituationTypeFamily,
    label: "Family owned",
  },
  {
    value: DomainLandSituationType.LandSituationReservation,
    label: "The land is in a reservation",
  },
];

// ============================================================================
// ECOA — Ethnicity sub-options (Hispanic/Latino children)
// ============================================================================

export const ETHNICITY_HISPANIC_CHILDREN = [
  { type: DomainEthnicityType.EthnicityMexican, label: "Mexican" },
  { type: DomainEthnicityType.EthnicityPuertoRican, label: "Puerto Rican" },
  { type: DomainEthnicityType.EthnicityCuban, label: "Cuban" },
  {
    type: DomainEthnicityType.EthnicityOtherHispanicOrLatino,
    label: "Other",
  },
] as const;

// ============================================================================
// ECOA — Race sub-options (Asian children)
// ============================================================================

export const RACE_ASIAN_CHILDREN = [
  { type: DomainRaceType.RaceAsianIndian, label: "Asian Indian" },
  { type: DomainRaceType.RaceChinese, label: "Chinese" },
  { type: DomainRaceType.RaceFilipino, label: "Filipino" },
  { type: DomainRaceType.RaceJapanese, label: "Japanese" },
  { type: DomainRaceType.RaceKorean, label: "Korean" },
  { type: DomainRaceType.RaceVietnamese, label: "Vietnamese" },
  { type: DomainRaceType.RaceOtherAsian, label: "Other" },
] as const;

// ============================================================================
// ECOA — Race sub-options (Native Hawaiian / Pacific Islander children)
// ============================================================================

export const RACE_PACIFIC_ISLANDER_CHILDREN = [
  { type: DomainRaceType.RaceNativeHawaiian, label: "Native Hawaiian" },
  {
    type: DomainRaceType.RaceGuamanianChamorro,
    label: "Guamanian or Chamorro",
  },
  { type: DomainRaceType.RaceSamoan, label: "Samoan" },
  { type: DomainRaceType.RaceOtherPacificIslander, label: "Other" },
] as const;

// ============================================================================
// Manufacturer
// ============================================================================

export const MANUFACTURER_TYPE_OPTIONS = [
  { value: DomainManufacturerType.ManufacturerCavco, label: "Cavco" },
  { value: DomainManufacturerType.ManufacturerChampion, label: "Champion" },
  { value: DomainManufacturerType.ManufacturerClayton, label: "Clayton" },
  { value: DomainManufacturerType.ManufacturerFleetwood, label: "Fleetwood" },
  { value: DomainManufacturerType.ManufacturerJessup, label: "Jessup" },
  { value: DomainManufacturerType.ManufacturerLegacy, label: "Legacy" },
  {
    value: DomainManufacturerType.ManufacturerMeridianCompassOakCreek,
    label: "Meridian / Compass / Oak Creek",
  },
  {
    value: DomainManufacturerType.ManufacturerPalmHarbor,
    label: "Palm Harbor",
  },
  { value: DomainManufacturerType.ManufacturerOakCreek, label: "Oak Creek" },
  {
    value: DomainManufacturerType.ManufacturerSouthernEnergy,
    label: "Southern Energy",
  },
  { value: DomainManufacturerType.ManufacturerTRU, label: "TRU" },
  { value: DomainManufacturerType.ManufacturerOther, label: "Other" },
];

// ============================================================================
// Down Payment Source
// ============================================================================

export const DOWN_PAYMENT_SOURCE_OPTIONS = [
  {
    value: DomainDownPaymentSourceType.DownPaymentSourceCheckingAccount,
    label: "Checking Account",
  },
  {
    value: DomainDownPaymentSourceType.DownPaymentSourceSavingsAccount,
    label: "Savings Account",
  },
  { value: DomainDownPaymentSourceType.DownPaymentSourceCash, label: "Cash" },
  {
    value: DomainDownPaymentSourceType.DownPaymentSourceLoan,
    label: "Loan",
  },
  {
    value: DomainDownPaymentSourceType.DownPaymentSourceGift,
    label: "Gift",
  },
  {
    value: DomainDownPaymentSourceType.DownPaymentSourceOther,
    label: "Other",
  },
];
